import { useEffect, useState } from "react";
import { api, ApiError } from "./api";
import { clearSnapshots } from "./snapshot";

export type AuthState = "unknown" | "logged_out" | "logged_in";

// Remembered auth result from the previous session: lets the app render the
// shell instantly on launch instead of blanking on a network roundtrip. The
// probe still runs and corrects the state (and clears caches) if the session
// actually expired.
const AUTH_FLAG = "caloly_authed";

function rememberAuthed(yes: boolean): void {
  try {
    if (yes) localStorage.setItem(AUTH_FLAG, "1");
    else localStorage.removeItem(AUTH_FLAG);
  } catch { /* storage unavailable */ }
  // Every page snapshot goes with the session. Clearing the namespace rather
  // than a named list is the point: a list drifts as caches are added, and the
  // failure mode is one account's data appearing under another's.
  if (!yes) clearSnapshots();
}

function wasAuthed(): boolean {
  try { return localStorage.getItem(AUTH_FLAG) === "1"; } catch { return false; }
}

export function useAuthProbe(): {
  state: AuthState;
  refresh: () => void;
} {
  const [state, setState] = useState<AuthState>(() => (wasAuthed() ? "logged_in" : "unknown"));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.getGoals();
        if (!cancelled) { rememberAuthed(true); setState("logged_in"); }
      } catch (e) {
        if (cancelled) return;
        if (!(e instanceof ApiError && (e.status === 401 || e.status === 403))) {
          // Network error — keep any optimistic logged_in state rather than
          // blanking the app while offline.
          if (!wasAuthed()) setState("unknown");
          return;
        }
        rememberAuthed(false);
        setState("logged_out");
      }
    })();
    return () => { cancelled = true; };
  }, [tick]);

  return {
    state,
    refresh: () => {
      // A refresh follows an explicit auth change (login/logout) — drop the
      // optimistic state and re-probe for real.
      rememberAuthed(false);
      setState("unknown");
      setTick((t) => t + 1);
    },
  };
}
